"use client";

import { useCallback, useId, useMemo, useSyncExternalStore } from "react";
import Particles, { ParticlesProvider } from "@tsparticles/react";
import type { Container, Engine, ISourceOptions } from "@tsparticles/engine";
import { loadSlim } from "@tsparticles/slim";
import { cn } from "@/lib/utils";
import { motion, useAnimation } from "motion/react";
import styles from "./sparkles.module.css";

type ParticlesProps = {
  id?: string;
  className?: string;
  background?: string;
  particleSize?: number;
  minSize?: number;
  maxSize?: number;
  speed?: number;
  particleColor?: string;
  particleDensity?: number;
};

function subscribeReducedMotion(onStoreChange: () => void) {
  const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
  mq.addEventListener("change", onStoreChange);
  return () => mq.removeEventListener("change", onStoreChange);
}

function getReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function subscribeMobile(onStoreChange: () => void) {
  const mq = window.matchMedia("(max-width: 600px)");
  mq.addEventListener("change", onStoreChange);
  return () => mq.removeEventListener("change", onStoreChange);
}

function getMobile() {
  return window.matchMedia("(max-width: 600px)").matches;
}

async function registerSlim(engine: Engine) {
  await loadSlim(engine);
}

function SparklesCanvas({
  id,
  className,
  background,
  minSize,
  maxSize,
  speed,
  particleColor,
  particleDensity,
}: ParticlesProps) {
  const controls = useAnimation();
  const generatedId = useId();
  const reduced = useSyncExternalStore(
    subscribeReducedMotion,
    getReducedMotion,
    () => false,
  );
  const mobile = useSyncExternalStore(subscribeMobile, getMobile, () => false);

  const particlesLoaded = useCallback(
    async (container?: Container) => {
      if (!container) return;
      controls.start({
        opacity: 1,
        transition: { duration: 1 },
      });
    },
    [controls],
  );

  const density = mobile
    ? Math.min(particleDensity || 120, 280)
    : particleDensity || 120;

  const options = useMemo<ISourceOptions>(
    () => ({
      background: {
        color: { value: background || "transparent" },
      },
      fullScreen: {
        enable: false,
        zIndex: 1,
      },
      fpsLimit: mobile ? 30 : 60,
      detectRetina: !mobile,
      particles: {
        color: { value: particleColor || "#ffffff" },
        move: {
          enable: !reduced,
          direction: "none",
          random: false,
          speed: { min: 0.1, max: 1 },
          outModes: { default: "out" },
        },
        number: {
          density: { enable: true, width: 400, height: 400 },
          value: reduced ? 40 : density,
        },
        opacity: {
          value: { min: 0.35, max: 1 },
          animation: {
            enable: !reduced,
            speed: speed || 4,
            sync: false,
            startValue: "random",
          },
        },
        shape: { type: "circle" },
        size: {
          value: { min: minSize || 1, max: maxSize || 3 },
        },
      },
    }),
    [
      background,
      density,
      maxSize,
      minSize,
      mobile,
      particleColor,
      reduced,
      speed,
    ],
  );

  if (reduced) {
    return <div className={cn(styles.root, className)} aria-hidden />;
  }

  return (
    <motion.div animate={controls} className={cn(styles.root, className)}>
      <Particles
        id={id || generatedId}
        className={styles.canvas}
        particlesLoaded={particlesLoaded}
        options={options}
      />
    </motion.div>
  );
}

export const SparklesCore = (props: ParticlesProps) => {
  return (
    <ParticlesProvider init={registerSlim}>
      <SparklesCanvas {...props} />
    </ParticlesProvider>
  );
};
